import { useState, Dispatch, SetStateAction, useEffect } from 'react'
import { UseFormMethods } from 'react-hook-form'

interface BasicFormSchema {
  name: string
  address: BasicAddress
  belongsToShipper?: boolean
}

interface BasicAddress {
  addressLine1?: string
  postalCode?: string
  countryCode?: string
  location?: {
    id?: string
    locality: string
    subdivisionCode: string
  }
}

interface AutoCompleteHookProps {
  /**
   * Default values in the form fields, if available
   */
  defaultValues?: BasicFormSchema
  /**
   * setValue function returned from the useForm hook.
   */
  setValue: UseFormMethods<BasicFormSchema>['setValue']
}

interface AutoCompleteHooksReturnProps {
  /**
   * The entire state of the form
   */
  facility?: Partial<BasicFormSchema>
  /**
   * set state callback, use this to pass the address data returned from an
   * address auto complete component into the logic in this hook.
   */
  setAddress: Dispatch<SetStateAction<BasicAddress | undefined>>
  /**
   * set state callback for the facility name, use this to pass the facility
   * name into the logic in this hook.
   */
  setName: Dispatch<SetStateAction<string | undefined>>
}

export function useFacilityFormAutoComplete({
  defaultValues,
  setValue,
}: AutoCompleteHookProps): AutoCompleteHooksReturnProps {
  const [name, setName] = useState<string | undefined>(defaultValues?.name)
  const [address, setAddress] = useState<BasicAddress | undefined>(
    defaultValues
      ? {
          ...defaultValues?.address,
        }
      : undefined
  )

  /**
   * Watch for change in `address` state object, and update RHF's form state
   */
  useEffect(() => {
    if (typeof address?.addressLine1 !== 'undefined') {
      setValue('address.addressLine1', address?.addressLine1, {
        shouldDirty: !!address?.addressLine1,
        shouldValidate: !!address?.addressLine1,
      })
    }

    if (address?.postalCode) {
      setValue('address.postalCode', address?.postalCode, {
        shouldDirty: !!address?.postalCode,
        shouldValidate: !!address?.postalCode,
      })
    }

    if (address?.location) {
      setValue(
        'address.location',
        {
          id: address?.location?.id,
          locality: address?.location?.locality,
          subdivisionCode: address?.location?.subdivisionCode,
        },
        {
          shouldDirty: !!address?.location?.locality,
          shouldValidate: !!address?.location?.locality,
        }
      )
    }

    if (address?.countryCode) {
      /**
       * this input is hidden to the user, no need to validate nor trigger dirty state
       */
      setValue('address.countryCode', address?.countryCode)
    }

    if (name) {
      setValue('name', name, {
        shouldDirty: !!name,
        shouldValidate: !!name,
      })
    }
  }, [address, setValue, name])

  return {
    facility: { ...defaultValues, name, address },
    setAddress,
    setName,
  }
}
